import { useCallback, useEffect, useMemo, useState } from 'react';

import { GridApi } from 'ag-grid-community';
import { Dropdown } from 'primereact/dropdown';
import { useSearchParams } from 'react-router-dom';

import { cn } from '@/lib/utils';
import { AspirationSite, Site } from '@/rtk-query.gql';
import { ISite } from '@/services/auth';
import { useSites } from '@/services/sites';

export type Selection = Partial<Site> | Partial<AspirationSite> | null;

type Props = {
  value?: Selection | null;
  onChange?: (s: Selection | null) => void;
  gridApi?: GridApi | null;
  field: string;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  matchMode?: 'equals' | 'contains';
  paramKey?: string;
  validSites?: Site[] | ISite[];
  label?: string;
  showAllLocations?: boolean;
  disableUrlSync?: boolean;
  alwaysSyncSelection?: boolean;
};

export const SitesDropdown = ({
  value = null,
  onChange,
  gridApi,
  field,
  placeholder = 'Select site',
  className,
  disabled,
  matchMode = 'contains',
  paramKey = 'sId',
  validSites,
  label,
  showAllLocations = false,
  disableUrlSync = false,
  alwaysSyncSelection = false,
}: Props) => {
  const [selected, setSelected] = useState<Selection>(value);
  const { data: aspirationSiteOptions, isLoading: isFetching } = useSites({
    filter: showAllLocations ? 'aspiration-locations' : 'aspiration',
  });

  const [searchParams, setSearchParams] = useSearchParams();

  const mappedValidSites = useMemo(
    () =>
      validSites?.map((site) => ({
        id: 'code' in site ? site.code : site.id,
        name: site.name,
        // user.sites only includes a fert site id
        fertilizationSiteId: 'fertSite' in site ? site.fertSite : undefined,
      })),
    [validSites],
  );

  const options = useMemo(
    () => mappedValidSites ?? aspirationSiteOptions,
    [mappedValidSites, aspirationSiteOptions],
  );

  const changeHandler = useCallback((site: Selection) => {
    onChange?.(site);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!options?.length) return;

    if (value?.id) {
      const found = options.find((s) => s.id === value.id);
      if (found) {
        if (
          alwaysSyncSelection ||
          found.id !== selected?.id ||
          !selected?.name
        ) {
          setSelected(found);
        }
        return;
      }
    }

    if (!selected && !disableUrlSync) {
      const idInUrl = searchParams.get(paramKey);
      if (idInUrl) {
        const found = options.find((s) => s.id === idInUrl);
        if (found) {
          setSelected(found);
          changeHandler?.(found);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    options,
    value,
    paramKey,
    searchParams,
    changeHandler,
    alwaysSyncSelection,
  ]);

  useEffect(() => {
    if (!gridApi) return;

    gridApi.setFilterModel(
      selected ? { [field]: { type: matchMode, filter: selected.name } } : null,
    );
    gridApi.onFilterChanged();
  }, [selected, gridApi, field, matchMode]);

  const commit = (site: Selection | null) => {
    if (!disableUrlSync) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (site?.id) {
            next.set(paramKey, site.id);
          } else {
            next.delete(paramKey);
          }
          return next;
        },
        { replace: true },
      );
    }

    setSelected(site);
    changeHandler?.(site);
  };

  return (
    <div className={cn(`${label ? 'label-input-group' : ''} ${className}`)}>
      {label && <label>{label}</label>}
      <Dropdown
        data-testid="sites-dropdown"
        value={selected}
        options={options}
        optionLabel="name"
        placeholder={placeholder}
        loading={isFetching}
        disabled={disabled}
        className="w-full"
        showClear
        filter
        virtualScrollerOptions={{ itemSize: 32 }}
        scrollHeight="400px"
        onChange={(e) => commit(e.value ?? null)}
        filterInputAutoFocus={true}
      />
    </div>
  );
};
